import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import request from '../helpers/request';

const API_BASE = '/api/v1/users';

/**
 * Custom hook for managing users (admin functionality)
 * @returns {object} Users state and operations
 */
export const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all users
  const fetchUsers = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.role) queryParams.append('role', params.role);
      if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);

      const queryString = queryParams.toString();
      const url = queryString ? `${API_BASE}/?${queryString}` : `${API_BASE}/`;
      
      const response = await request.get(url);
      // Handle both paginated and non-paginated responses
      const data = response.data.results || response.data;
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch users', err);
      const message = err.response?.data?.error || 'Failed to load users.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new user
  const createUser = useCallback(async (data) => {
    try {
      const response = await request.post(`${API_BASE}/`, data);
      toast.success(`User "${data.username}" created successfully!`);
      await fetchUsers();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error creating user:', err);
      const errors = err.response?.data;
      if (errors && typeof errors === 'object') {
        // Return field-level errors
        return { success: false, errors };
      }
      const message = err.response?.data?.error || 'Failed to create user.';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [fetchUsers]);

  // Update an existing user
  const updateUser = useCallback(async (id, data) => {
    try {
      const response = await request.patch(`${API_BASE}/${id}/`, data);
      toast.success('User updated successfully!');
      await fetchUsers();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error updating user:', err);
      const errors = err.response?.data;
      if (errors && typeof errors === 'object') {
        return { success: false, errors };
      }
      const message = err.response?.data?.error || 'Failed to update user.';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [fetchUsers]);

  // Delete a user
  const deleteUser = useCallback(async (id) => {
    try {
      await request.delete(`${API_BASE}/${id}/`);
      toast.warn('User deleted!');
      await fetchUsers();
      return { success: true };
    } catch (err) {
      console.error('Error deleting user:', err);
      const message = err.response?.data?.error || err.response?.data?.detail || 'Failed to delete user.';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [fetchUsers]);

  // Set user password (admin)
  const setUserPassword = useCallback(async (id, password) => {
    try {
      await request.post(`${API_BASE}/${id}/set-password/`, { password });
      toast.success('Password updated successfully!');
      return { success: true };
    } catch (err) {
      console.error('Error setting user password:', err);
      const message = err.response?.data?.error || 'Failed to update password.';
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  // Toggle user active status
  const toggleUserActive = useCallback(async (id, isActive) => {
    try {
      await request.patch(`${API_BASE}/${id}/`, { is_active: isActive });
      toast.success(`User ${isActive ? 'activated' : 'deactivated'}!`);
      await fetchUsers();
      return { success: true };
    } catch (err) {
      console.error('Error toggling user status:', err);
      const message = err.response?.data?.error || 'Failed to update user status.';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [fetchUsers]);

  // Initial fetch on mount
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    setUserPassword,
    toggleUserActive,
  };
};

export default useUsers;
